import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const api = axios.create({ baseURL: API_URL });

const ActivityLogScreen = () => {
  const { isAdmin, token } = useAuth();

  // Filter state
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Data state
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [hideBots, setHideBots] = useState(true);

  // Set up auth header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (action) params.action = action;
      if (entityType) params.entity_type = entityType;
      if (userId) params.user_id = userId;
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (hideBots) params.hide_bots = 'true';

      const response = await api.get('/activity', { params });
      setActivities(response.data.activities || []);
      setPagination(response.data.pagination || { total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoading(false);
    }
  }, [page, action, entityType, userId, search, startDate, endDate, hideBots]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (hideBots) params.hide_bots = 'true';
      const response = await api.get('/activity/stats', { params });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch activity stats:', err);
    }
  }, [hideBots]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchActivities();
  };

  const handleClearFilters = () => {
    setAction('');
    setEntityType('');
    setUserId('');
    setUserName('');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setHideBots(true);
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric',
      minute: '2-digit', hour12: true
    });
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
    });
  };

  const actionColor = (act) => {
    switch (act) {
      case 'login': return { bg: '#ebf8ff', text: '#2b6cb0', border: '#bee3f8' };
      case 'login_failed': return { bg: '#fff5f5', text: '#c53030', border: '#fed7d7' };
      case 'logout': return { bg: '#f7fafc', text: '#4a5568', border: '#e2e8f0' };
      case 'signup': return { bg: '#f0fff4', text: '#276749', border: '#c6f6d5' };
      case 'signup_warning': return { bg: '#fffbeb', text: '#92400e', border: '#fde68a' };
      case 'email_verified': return { bg: '#f0fff4', text: '#276749', border: '#c6f6d5' };
      case 'email_verification_failed': return { bg: '#fff5f5', text: '#c53030', border: '#fed7d7' };
      case 'email_changed': return { bg: '#ebf8ff', text: '#2b6cb0', border: '#bee3f8' };
      case 'page_view': return { bg: '#fefce8', text: '#a16207', border: '#fef08a' };
      case 'navigate': return { bg: '#fefce8', text: '#a16207', border: '#fef08a' };
      case 'view': return { bg: '#f0f4ff', text: '#3b82f6', border: '#dbeafe' };
      case 'search': return { bg: '#fdf4ff', text: '#a855f7', border: '#f3e8ff' };
      case 'create': return { bg: '#f0fff4', text: '#276749', border: '#c6f6d5' };
      case 'update': return { bg: '#fffff0', text: '#b7791f', border: '#fefcbf' };
      case 'delete': return { bg: '#fff5f5', text: '#c53030', border: '#fed7d7' };
      case 'admin_action': return { bg: '#faf5ff', text: '#6b46c1', border: '#e9d8fd' };
      case 'valuate': return { bg: '#ebf8ff', text: '#2563eb', border: '#bee3f8' };
      case 'subscription_activated': return { bg: '#f0fff4', text: '#276749', border: '#c6f6d5' };
      case 'subscription_canceled': return { bg: '#fff5f5', text: '#c53030', border: '#fed7d7' };
      case 'server_start': return { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' };
      default: return { bg: '#f7fafc', text: '#4a5568', border: '#e2e8f0' };
    }
  };

  const entityIcon = (type) => {
    switch (type) {
      case 'amplifier': return '🎸';
      case 'manufacturer': return '🏭';
      case 'collection': return '📦';
      case 'discussion_topic': return '💬';
      case 'discussion_message': return '💬';
      case 'artifact': return '📎';
      case 'billing': return '💳';
      case 'checkout': return '💳';
      case 'showcase': return '🖼️';
      case 'page': return '📄';
      case 'user': return '👤';
      case 'system': return '⚙️';
      default: return '📋';
    }
  };

  if (!isAdmin) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Admin access required</div>;
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#333', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
          Activity Log
        </h1>
        <p style={{ color: '#888', fontSize: '0.82rem', margin: 0 }}>
          Track user actions and system events across the platform
        </p>
      </div>

      {/* Stats Dashboard */}
      {stats?.summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <StatCard label="Last 24h" value={stats.summary.last_24h} color="#2563eb" />
          <StatCard label="Active Users (24h)" value={stats.summary.active_users_24h} color="#16a34a" />
          <StatCard label="Last 7 Days" value={stats.summary.last_7d} color="#7c3aed" />
          <StatCard label="Active Users (7d)" value={stats.summary.active_users_7d} color="#059669" />
          <StatCard label="Last 30 Days" value={stats.summary.last_30d} color="#555" />
          <StatCard label="All Time" value={stats.summary.total} color="#888" />
        </div>
      )}

      {/* Top Actions (last 24h) */}
      {stats?.topActions?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div style={{
            backgroundColor: '#f0f4ff', border: '1px solid #dbeafe', borderRadius: '8px',
            padding: '16px'
          }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#2563eb', margin: '0 0 10px 0' }}>
              🔥 Top Actions (Last 24h)
            </h3>
            {stats.topActions.slice(0, 5).map((a, i) => (
              <div key={i} onClick={() => { setAction(a.action); setEntityType(a.entity_type); setPage(1); }} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: i < Math.min(stats.topActions.length, 5) - 1 ? '1px solid #dbeafe' : 'none',
                cursor: 'pointer', borderRadius: '4px', transition: 'background-color 0.15s'
              }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#dbeafe'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '0.8rem', color: '#1e40af' }}>
                  {a.action} → {a.entity_type}
                </span>
                <span style={{
                  backgroundColor: '#2563eb', color: 'white', borderRadius: '10px',
                  padding: '2px 8px', fontSize: '0.7rem', fontWeight: '600', flexShrink: 0
                }}>
                  {a.count}
                </span>
              </div>
            ))}
          </div>

          {/* Most Active Users (7d) */}
          {stats?.topUsers?.length > 0 && (
            <div style={{
              backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px',
              padding: '16px'
            }}>
              <h3 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#16a34a', margin: '0 0 10px 0' }}>
                👥 Most Active Users (Last 7d)
              </h3>
              {stats.topUsers.slice(0, 5).map((u, i) => {
                const isAnon = u.user_id == null;
                return (
                <div key={i}
                  onClick={isAnon ? undefined : () => { setUserId(String(u.user_id)); setUserName(u.username || `User #${u.user_id}`); setPage(1); }}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0', borderBottom: i < Math.min(stats.topUsers.length, 5) - 1 ? '1px solid #bbf7d0' : 'none',
                    cursor: isAnon ? 'default' : 'pointer', borderRadius: '4px', transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={isAnon ? undefined : e => e.currentTarget.style.backgroundColor = '#bbf7d0'}
                  onMouseLeave={isAnon ? undefined : e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: '0.8rem', color: isAnon ? '#6c757d' : '#166534', fontStyle: isAnon ? 'italic' : 'normal' }}>
                    {isAnon ? (u.ip_address ? `anon: ${u.ip_address}` : '—') : (u.username || `User #${u.user_id}`)}
                  </span>
                  <span style={{
                    backgroundColor: '#16a34a', color: 'white', borderRadius: '10px',
                    padding: '2px 8px', fontSize: '0.7rem', fontWeight: '600', flexShrink: 0
                  }}>
                    {u.action_count}
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Active Filter Chips */}
      {(action || entityType || userId || !hideBots) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: '600' }}>Active filters:</span>
          {action && (
            <FilterChip label={`Action: ${action}`} onRemove={() => { setAction(''); setPage(1); }} />
          )}
          {entityType && (
            <FilterChip label={`Entity: ${entityType}`} onRemove={() => { setEntityType(''); setPage(1); }} />
          )}
          {userId && (
            <FilterChip label={`User: ${userName || userId}`} onRemove={() => { setUserId(''); setUserName(''); setPage(1); }} />
          )}
          {!hideBots && (
            <FilterChip label="Showing bots" onRemove={() => { setHideBots(true); setPage(1); }} />
          )}
          <button onClick={handleClearFilters} style={{
            background: 'none', border: 'none', color: '#2563eb', fontSize: '0.75rem',
            cursor: 'pointer', fontWeight: '500', textDecoration: 'underline'
          }}>
            Clear all
          </button>
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleSearchSubmit} style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px',
        padding: '16px', backgroundColor: 'white', borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <select value={action} onChange={handleFilterChange(setAction)} style={filterSelectStyle}>
          <option value="">All Actions</option>
          <option value="page_view">Page View</option>
          <option value="login">Login</option>
          <option value="login_failed">Login Failed</option>
          <option value="logout">Logout</option>
          <option value="signup">Signup</option>
          <option value="signup_warning">Signup Warning</option>
          <option value="email_verified">Email Verified</option>
          <option value="email_verification_failed">Email Verification Failed</option>
          <option value="email_changed">Email Changed</option>
          <option value="navigate">Navigate (API)</option>
          <option value="view">View</option>
          <option value="search">Search</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="valuate">Valuate</option>
          <option value="admin_action">Admin Action</option>
          <option value="server_start">Server Start</option>
          <option value="subscription_activated">Subscription Activated</option>
          <option value="subscription_canceled">Subscription Canceled</option>
        </select>

        <select value={entityType} onChange={handleFilterChange(setEntityType)} style={filterSelectStyle}>
          <option value="">All Entity Types</option>
          <option value="page">Page</option>
          <option value="user">User</option>
          <option value="amplifier">Amplifier</option>
          <option value="manufacturer">Manufacturer</option>
          <option value="collection">Collection</option>
          <option value="discussion_topic">Discussion Topic</option>
          <option value="discussion_message">Discussion Message</option>
          <option value="artifact">Artifact</option>
          <option value="billing">Billing</option>
          <option value="checkout">Checkout</option>
          <option value="showcase">Showcase</option>
          <option value="system">System</option>
        </select>

        <input
          type="date"
          value={startDate}
          onChange={handleFilterChange(setStartDate)}
          style={filterInputStyle}
          placeholder="Start date"
        />
        <input
          type="date"
          value={endDate}
          onChange={handleFilterChange(setEndDate)}
          style={filterInputStyle}
          placeholder="End date"
        />

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search descriptions, names, URLs..."
          style={{ ...filterInputStyle, flex: '1 1 200px' }}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#555', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={hideBots}
            onChange={e => { setHideBots(e.target.checked); setPage(1); }}
            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
          />
          Hide bots
        </label>
        <button type="submit" style={filterButtonStyle}>
          Search
        </button>
        <button type="button" onClick={handleClearFilters} style={clearButtonStyle}>
          Clear
        </button>
      </form>

      {/* Results count */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '0.8rem', color: '#888' }}>
          {pagination.total} activit{pagination.total !== 1 ? 'ies' : 'y'} found
          {pagination.totalPages > 1 && ` • Page ${page} of ${pagination.totalPages}`}
        </span>
        <button onClick={() => { fetchActivities(); fetchStats(); }} style={{
          background: 'none', border: 'none', color: '#2563eb', fontSize: '0.8rem',
          cursor: 'pointer', fontWeight: '500'
        }}>
          ↻ Refresh
        </button>
      </div>

      {/* Activity List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
      ) : activities.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px', backgroundColor: 'white',
          borderRadius: '8px', border: '1px solid #e9ecef'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
          <p style={{ color: '#555', fontSize: '0.9rem' }}>No activities found</p>
          <p style={{ color: '#888', fontSize: '0.8rem' }}>Activity will appear here as users interact with the platform</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {activities.map((act) => {
            const ac = actionColor(act.action);
            const isSelected = selectedActivity?.activity_id === act.activity_id;
            return (
              <div key={act.activity_id}>
                {/* Summary row */}
                <div
                  onClick={() => setSelectedActivity(isSelected ? null : act)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px', backgroundColor: 'white',
                    borderRadius: isSelected ? '8px 8px 0 0' : '8px',
                    border: `1px solid ${isSelected ? '#2563eb' : '#e9ecef'}`,
                    borderBottom: isSelected ? '1px dashed #dee2e6' : undefined,
                    cursor: 'pointer', transition: 'border-color 0.15s ease'
                  }}
                >
                  {/* Entity icon */}
                  <span style={{ fontSize: '1rem', flexShrink: 0 }}>
                    {entityIcon(act.entity_type)}
                  </span>

                  {/* Action badge */}
                  <span style={{
                    backgroundColor: ac.bg, color: ac.text, border: `1px solid ${ac.border}`,
                    borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem',
                    fontWeight: '600', textTransform: 'uppercase', flexShrink: 0,
                    minWidth: '50px', textAlign: 'center'
                  }}>
                    {act.action}
                  </span>

                  {/* Entity type */}
                  <span style={{
                    backgroundColor: '#f0f4ff', color: '#2563eb', borderRadius: '4px',
                    padding: '2px 8px', fontSize: '0.7rem', fontWeight: '500', flexShrink: 0
                  }}>
                    {act.entity_type}
                  </span>

                  {/* Description / entity name + request URL for navigation */}
                  <span style={{
                    flex: 1, fontSize: '0.82rem', color: '#333', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px'
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {act.description || act.entity_name || '—'}
                    </span>
                    {act.request_url && (
                      <span style={{
                        fontSize: '0.7rem', color: '#94a3b8', flexShrink: 0,
                        maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontFamily: 'monospace'
                      }}>
                        {act.request_url.replace(/\?.*$/, '')}
                      </span>
                    )}
                  </span>

                  {/* Username / IP for anon */}
                  <span
                    style={{ fontSize: '0.75rem', color: act.username ? '#666' : '#a0aec0', flexShrink: 0, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={act.username ? undefined : (act.ip_address ? `Anonymous — ${act.ip_address}` : 'Anonymous')}
                  >
                    {act.username || (act.ip_address ? `anon: ${act.ip_address}` : '—')}
                  </span>

                  {/* Timestamp */}
                  <span style={{ fontSize: '0.72rem', color: '#aaa', flexShrink: 0, minWidth: '90px', textAlign: 'right' }}>
                    {formatDate(act.created_at)}
                  </span>
                </div>

                {/* Detail panel (expanded) */}
                {isSelected && (
                  <div style={{
                    padding: '16px', backgroundColor: '#fafbfc',
                    border: '1px solid #2563eb', borderTop: 'none',
                    borderRadius: '0 0 8px 8px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                      <DetailField label="Activity ID" value={act.activity_id} />
                      <DetailField label="Timestamp" value={formatDateFull(act.created_at)} />
                      <DetailField label="Action" value={act.action} />
                      <DetailField label="Entity Type" value={act.entity_type} />
                      {act.entity_id && <DetailField label="Entity ID" value={act.entity_id} />}
                      {act.entity_name && <DetailField label="Entity Name" value={act.entity_name} />}
                      {act.username
                        ? <DetailField label="User" value={`${act.username} (ID: ${act.user_id})`} />
                        : <DetailField label="User" value={act.ip_address ? `Anonymous (${act.ip_address})` : 'Anonymous'} />
                      }
                      {act.ip_address && <DetailField label="IP Address" value={act.ip_address} />}
                      {act.request_method && <DetailField label="Request" value={`${act.request_method} ${act.request_url}`} />}
                    </div>

                    {act.description && (
                      <DetailField label="Description" value={act.description} fullWidth />
                    )}

                    {act.user_agent && (
                      <div style={{ marginTop: '12px' }}>
                        <DetailField label="User Agent" value={act.user_agent} fullWidth />
                      </div>
                    )}

                    {act.metadata && Object.keys(act.metadata).length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        {/* Show console_output as formatted terminal block */}
                        {act.metadata.console_output && (
                          <div style={{ marginBottom: '12px' }}>
                            <label style={detailLabelStyle}>Console Output</label>
                            <pre style={{
                              ...preStyle,
                              backgroundColor: '#1e1e1e',
                              color: '#d4d4d4',
                              fontFamily: "'Menlo', 'Consolas', 'Courier New', monospace",
                              fontSize: '0.8rem',
                              lineHeight: '1.5',
                              padding: '12px',
                              borderRadius: '6px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxHeight: '400px',
                              overflowY: 'auto'
                            }}>{act.metadata.console_output}</pre>
                          </div>
                        )}
                        {/* Show remaining metadata fields (excluding console_output) as JSON */}
                        {(() => {
                          const { console_output, ...rest } = act.metadata;
                          return Object.keys(rest).length > 0 ? (
                            <div>
                              <label style={detailLabelStyle}>Metadata</label>
                              <pre style={preStyle}>{JSON.stringify(rest, null, 2)}</pre>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={paginationButtonStyle(page === 1)}
          >
            ← Prev
          </button>
          {generatePageNumbers(page, pagination.totalPages).map((p, i) => (
            p === '...' ? (
              <span key={`dots-${i}`} style={{ padding: '6px 4px', color: '#888', fontSize: '0.8rem' }}>…</span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  ...paginationButtonStyle(false),
                  backgroundColor: p === page ? '#2563eb' : 'white',
                  color: p === page ? 'white' : '#333',
                  borderColor: p === page ? '#2563eb' : '#e9ecef',
                  fontWeight: p === page ? '600' : '400'
                }}
              >
                {p}
              </button>
            )
          ))}
          <button
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            style={paginationButtonStyle(page === pagination.totalPages)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────

const StatCard = ({ label, value, color }) => (
  <div style={{
    backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef',
    padding: '14px 16px', textAlign: 'center'
  }}>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color, lineHeight: 1 }}>{value || 0}</div>
    <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '4px' }}>{label}</div>
  </div>
);

const DetailField = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label style={detailLabelStyle}>{label}</label>
    <div style={{ fontSize: '0.82rem', color: '#333', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

const FilterChip = ({ label, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
    borderRadius: '16px', padding: '3px 10px', fontSize: '0.75rem', fontWeight: '500'
  }}>
    {label}
    <button onClick={onRemove} style={{
      background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer',
      fontSize: '0.85rem', padding: 0, lineHeight: 1, fontWeight: '700'
    }}>×</button>
  </span>
);

// ─── Style constants ─────────────────────────────────────────────

const detailLabelStyle = {
  display: 'block', fontSize: '0.7rem', color: '#888',
  fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em',
  marginBottom: '2px'
};

const preStyle = {
  backgroundColor: '#1a202c', color: '#e2e8f0', borderRadius: '6px',
  padding: '12px', fontSize: '0.72rem', lineHeight: '1.5',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: '300px', overflow: 'auto', margin: 0
};

const filterSelectStyle = {
  padding: '7px 10px', borderRadius: '6px', border: '1px solid #e9ecef',
  fontSize: '0.82rem', color: '#333', backgroundColor: 'white', cursor: 'pointer'
};

const filterInputStyle = {
  padding: '7px 10px', borderRadius: '6px', border: '1px solid #e9ecef',
  fontSize: '0.82rem', color: '#333'
};

const filterButtonStyle = {
  padding: '7px 18px', borderRadius: '6px', border: 'none',
  backgroundColor: '#2563eb', color: 'white', fontSize: '0.82rem',
  fontWeight: '500', cursor: 'pointer'
};

const clearButtonStyle = {
  padding: '7px 18px', borderRadius: '6px', border: '1px solid #e9ecef',
  backgroundColor: 'white', color: '#555', fontSize: '0.82rem',
  fontWeight: '500', cursor: 'pointer'
};

const paginationButtonStyle = (disabled) => ({
  padding: '6px 14px', borderRadius: '6px', border: '1px solid #e9ecef',
  backgroundColor: 'white', color: disabled ? '#ccc' : '#333',
  fontSize: '0.8rem', fontWeight: '500', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1
});

// Generate smart page number array with ellipsis
function generatePageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

export default ActivityLogScreen;
